import { useCallback, useEffect, useState } from 'react';
import { Calendar, Plus, Trash2, Copy, Check, Loader2, QrCode, X } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

interface TokenRow {
  id: string;
  token: string;
  description: string | null;
  scopes: string[];
  created_at: string;
  last_accessed_at: string | null;
}

const ALL_SCOPES: Array<{ key: string; label: string }> = [
  { key: 'personal', label: 'Eigene Termine' },
  { key: 'tasks',    label: 'Aufgaben' },
  { key: 'absences', label: 'Abwesenheiten' },
  { key: 'todos',    label: 'Todos' },
  // 2026-07-03: fuhrpark-Scope nachgezogen — Backend hatte die Quelle schon,
  // im Subscribe-Card fehlte sie. HU/AU-, Reifen- und Fuehrerschein-Termine
  // landen dadurch im iCal-Feed.
  { key: 'fuhrpark', label: 'Fuhrpark (HU/AU, Reifen, Fuehrerschein)' },
  // 2026-07-05: ressourcen-Scope. Wartungen aus ressourcen.maintenance_schedules.
  { key: 'ressourcen', label: 'Ressourcen (Wartungen, DGUV, Kalibrierung)' },
  // 2026-07-03: holidays-Scope. Region kommt vom Backend (TENANT_REGION, default NW).
  { key: 'holidays', label: 'Feiertage' },
];

export default function KalenderSubscribeCard() {
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>(ALL_SCOPES.map((s) => s.key));
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // 2026-07-03: QR-Code-Popup. openQr haelt die Token-Row des aktuell
  // angezeigten Abos, oder null wenn geschlossen.
  const [openQr, setOpenQr] = useState<TokenRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get<{ success: boolean; tokens: TokenRow[] }>('/kalender/subscribe-tokens');
      setTokens(r.tokens ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const create = async (): Promise<void> => {
    setCreating(true);
    setError('');
    try {
      await api.post('/kalender/subscribe-tokens', {
        description: newDesc.trim() || undefined,
        scopes: newScopes,
      });
      setNewDesc('');
      setNewScopes(ALL_SCOPES.map((s) => s.key));
      setShowNew(false);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (t: TokenRow): Promise<void> => {
    const label = t.description || `Token ${t.token.slice(0, 8)}...`;
    if (!confirm(`Kalender-Abo "${label}" loeschen? Alle abonnierenden Clients verlieren den Zugriff.`)) return;
    try {
      await api.delete(`/kalender/subscribe-tokens/${t.id}`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const subscribeUrl = (token: string): string => {
    return `${window.location.origin}/ical/${token}.ics`;
  };

  const copy = async (t: TokenRow): Promise<void> => {
    try {
      await navigator.clipboard.writeText(subscribeUrl(t.token));
      setCopiedId(t.id);
      setTimeout(() => setCopiedId((c) => (c === t.id ? null : c)), 1500);
    } catch {
      setError('Kopieren fehlgeschlagen — bitte URL manuell markieren');
    }
  };

  const toggleScope = (key: string): void => {
    setNewScopes((cur) => cur.includes(key) ? cur.filter((s) => s !== key) : [...cur, key]);
  };

  return (
    <div className="bg-slate-900 rounded-xl sm:rounded-lg p-5 sm:p-4 border border-slate-800 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-brand-400" />
          <span className="text-slate-100 font-semibold text-sm">Kalender-Abos (iCal)</span>
        </div>
        {!showNew && (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-brand-600 hover:bg-brand-500 text-primary-foreground"
          >
            <Plus size={14} /> Neu
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Abonniere deinen Kalender in Outlook, Google Kalender, Apple Kalender oder Thunderbird.
        Pro Endgeraet / App ein eigener Token — bei Verlust einfach loeschen.
      </p>

      {error && (
        <div className="px-3 py-2 rounded-md bg-rose-500/15 text-rose-300 text-sm">{error}</div>
      )}

      {showNew && (
        <div className="rounded-md border border-slate-700 bg-slate-800/50 p-3 space-y-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Beschreibung (optional)</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="z.B. iPhone, Outlook-Buero"
              className="w-full h-10 px-3 rounded-md border border-slate-700 bg-slate-900 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1">Enthalten:</div>
            <div className="flex flex-wrap gap-2">
              {ALL_SCOPES.map((s) => {
                const on = newScopes.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggleScope(s.key)}
                    className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                      on
                        ? 'bg-brand-500/15 text-brand-200 border-brand-500/50'
                        : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowNew(false); setError(''); }}
              className="px-3 py-1.5 text-sm text-slate-300 hover:text-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void create()}
              disabled={creating || newScopes.length === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-brand-600 hover:bg-brand-500 text-primary-foreground disabled:opacity-50"
            >
              {creating && <Loader2 size={14} className="animate-spin" />}
              Abo anlegen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-4 justify-center text-sm">
          <Loader2 size={14} className="animate-spin" /> Laedt...
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center text-slate-500 py-4 text-xs">
          Noch keine Kalender-Abos angelegt.
        </div>
      ) : (
        <div className="space-y-2">
          {tokens.map((t) => (
            <div key={t.id} className="rounded-md border border-slate-800 bg-slate-800/30 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100 truncate">
                    {t.description || <span className="italic text-slate-400">Ohne Bezeichnung</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.scopes.map((s) => {
                      const meta = ALL_SCOPES.find((x) => x.key === s);
                      return (
                        <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-700 text-slate-300">
                          {meta?.label ?? s}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void remove(t)}
                  className="p-1.5 text-slate-400 hover:text-rose-400"
                  title="Abo loeschen"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={subscribeUrl(t.token)}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="flex-1 min-w-0 h-8 px-2 rounded text-xs bg-slate-900 border border-slate-700 text-slate-300 font-mono"
                />
                <button
                  type="button"
                  onClick={() => void copy(t)}
                  className="inline-flex items-center gap-1 px-2 h-8 rounded text-xs bg-slate-700 hover:bg-slate-600 text-slate-200"
                  title="URL in Zwischenablage kopieren"
                >
                  {copiedId === t.id ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId === t.id ? 'Kopiert' : 'Kopieren'}
                </button>
                <button
                  type="button"
                  onClick={() => setOpenQr(t)}
                  className="inline-flex items-center gap-1 px-2 h-8 rounded text-xs bg-slate-700 hover:bg-slate-600 text-slate-200"
                  title="QR-Code fuers Handy anzeigen"
                >
                  <QrCode size={12} /> QR
                </button>
              </div>
              <div className="text-[10px] text-slate-500">
                Angelegt {formatDate(t.created_at)}
                {t.last_accessed_at && <> · Zuletzt abgerufen {formatDate(t.last_accessed_at)}</>}
              </div>
            </div>
          ))}
        </div>
      )}

      {openQr && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setOpenQr(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-xs w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-100">
                {openQr.description || 'Kalender-Abo'}
              </div>
              <button
                type="button"
                onClick={() => setOpenQr(null)}
                className="text-slate-400 hover:text-slate-100"
                aria-label="Schliessen"
              >
                <X size={16} />
              </button>
            </div>
            <div className="rounded-lg bg-white p-3 flex items-center justify-center">
              <img
                src={`/api/kalender/subscribe-tokens/${openQr.id}/qr.svg`}
                alt="QR-Code fuers Kalender-Abo"
                className="w-full max-w-[240px] h-auto"
              />
            </div>
            <div className="text-xs text-slate-300 text-center">
              Mit dem Handy scannen → Kalender-App fragt „Abonnieren?"
            </div>
            <div className="text-[10px] text-slate-500 text-center font-mono break-all">
              {subscribeUrl(openQr.token)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
